import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { takeUntil } from 'rxjs';

import { BaseComponent } from '../../../../core/components/base/base.component';
import { FeedbackService } from '../../../../core/services/feedback.service';
import { PatientAppService, PatientAppShare, PatientAppSourceType } from '../../../../core/services/patient-app.service';

export interface PatientAppShareDialogData {
  patientId: string;
  patientName: string;
  entryTitle: string;
  sourceType: PatientAppSourceType;
  sourceId: string;
}

export interface PatientAppShareDialogResult {
  changed?: boolean;
}

/**
 * Sending ONE entry to the patient's own app (tasks/patient-app.md §6.1, P3, P13, P14): a frozen copy, re-sent when the
 * entry is edited (the patient sees "Edited on"). "Confidential to patient" (§4.1a) keeps it from a parent or proxy
 * who manages a 12-17 year old's profile. An administrator can withdraw what was sent (P13).
 */
@Component({
  selector: 'app-patient-app-share-dialog',
  templateUrl: './patient-app-share-dialog.component.html',
  styleUrls: ['../share-entry-dialog/share-entry-dialog.component.scss'],
})
export class PatientAppShareDialogComponent extends BaseComponent implements OnInit {
  isLoading = true;
  isSaving = false;
  loadError: string | null = null;
  actionError: string | null = null;
  linked = false;
  canShare = false;
  canWithdraw = false;
  current: PatientAppShare | null = null;
  confidential = false;

  constructor(
    private patientApp: PatientAppService,
    private dialogRef: MatDialogRef<PatientAppShareDialogComponent, PatientAppShareDialogResult>,
    @Inject(MAT_DIALOG_DATA) public data: PatientAppShareDialogData,
    fs: FeedbackService,
    md: MatDialog,
  ) {
    super(fs, md);
  }

  ngOnInit(): void {
    this.patientApp.listShares(this.data.patientId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (state) => {
        this.linked = state.linked;
        this.canShare = state.canShare;
        this.canWithdraw = state.canWithdraw;
        this.current = state.shares.find((s) => s.sourceType === this.data.sourceType && s.sourceId === this.data.sourceId.toLowerCase()) || null;
        this.confidential = this.current?.audience === 'patient_only';
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
        this.loadError = 'The patient app could not be reached. Please try again.';
      },
    });
  }

  get inApp(): boolean {
    return !!this.current && !this.current.withdrawnAt;
  }

  send(): void {
    if (this.isSaving || !this.canShare) return;
    this.isSaving = true;
    this.actionError = null;
    this.patientApp.share(this.data.patientId, this.data.sourceType, this.data.sourceId, this.confidential ? 'patient_only' : 'all')
      .pipe(takeUntil(this.destroy$)).subscribe({
        next: () => {
          this.isSaving = false;
          this.showToastMessage('Sent. It reaches the patient\'s app shortly.');
          this.dialogRef.close({ changed: true });
        },
        error: (error) => {
          this.isSaving = false;
          this.actionError = error?.error?.message || 'This entry could not be sent. Please try again.';
        },
      });
  }

  async withdraw(): Promise<void> {
    if (this.isSaving || !this.canWithdraw) return;
    const confirmed = await this.showConfirmationDialog(
      'Withdraw from the patient\'s app',
      'The patient\'s copy is removed from their app, and they are told the practice withdrew it. Use this for something sent in error.',
      undefined, 'Cancel', 'Withdraw',
    );
    if (!confirmed) return;
    this.isSaving = true;
    this.patientApp.withdraw(this.data.patientId, this.data.sourceType, this.data.sourceId).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.isSaving = false;
        this.showToastMessage('Withdrawn from the patient\'s app.');
        this.dialogRef.close({ changed: true });
      },
      error: (error) => {
        this.isSaving = false;
        this.actionError = error?.error?.message || 'This entry could not be withdrawn. Please try again.';
      },
    });
  }

  close(): void {
    this.dialogRef.close({});
  }
}
